import { useState, useMemo } from "react";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { toolProjects, PROJECTS_URL } from "../data/toolProjects";
import { FiSearch, FiX, FiArrowUpRight } from "react-icons/fi";

interface Tool {
  name: string;
}

interface Category {
  title: string;
  tools: Tool[];
}

const categories: Category[] = [
  {
    title: "Containers & Orchestration",
    tools: [
      { name: "Docker" },
      { name: "Docker Compose" },
      { name: "Kubernetes" },
      { name: "Kind" },
      { name: "Helm" },
      { name: "Traefik" },
      { name: "Nginx" },
      { name: "Linux" },
      { name: "systemd" },
    ],
  },
  {
    title: "Security & Zero-Trust",
    tools: [
      { name: "Teleport" },
      { name: "Zero-Trust" },
      { name: "RBAC" },
      { name: "TLS" },
      { name: "GitOps" },
    ],
  },
  {
    title: "Observability",
    tools: [
      { name: "Loki" },
      { name: "Grafana" },
      { name: "Grafana Alloy" },
      { name: "Prometheus" },
    ],
  },
  {
    title: "Cloud & Networking",
    tools: [
      { name: "Cloudflare" },
      { name: "Cloudflare Tunnel" },
      { name: "AWS" },
      { name: "EC2" },
      { name: "ECS" },
      { name: "EKS" },
      { name: "ECR" },
      { name: "VPC" },
      { name: "S3" },
      { name: "Lambda" },
      { name: "ElastiCache" },
      { name: "Secrets Manager" },
      { name: "SSM" },
      { name: "Aurora" },
      { name: "API Gateway" },
      { name: "WAF" },
      { name: "CloudFront" },
      { name: "RDS" },
      { name: "Route53" },
      { name: "Terraform" },
      { name: "CloudFormation" },
    ],
  },
  {
    title: "AI & LLMs",
    tools: [
      { name: "Claude Code" },
      { name: "LLM Agents" },
      { name: "OpenAI" },
      { name: "AI Ingest" },
    ],
  },
  {
    title: "Programming & Scripting",
    tools: [
      { name: "Python" },
      { name: "TypeScript" },
      { name: "Node.js" },
      { name: "Bash" },
      { name: "NestJS" },
    ],
  },
  {
    title: "Data & Storage",
    tools: [
      { name: "PostgreSQL" },
      { name: "Redis" },
      { name: "MongoDB" },
    ],
  },
  {
    title: "System Design",
    tools: [
      { name: "REST" },
      { name: "gRPC" },
      { name: "WebSockets" },
      { name: "GraphQL" },
    ],
  },
  {
    title: "Platform & CI/CD",
    tools: [
      { name: "GitHub Actions" },
      { name: "Bitbucket Pipelines" },
      { name: "Jenkins" },
      { name: "GitLab CI" },
      { name: "Nx monorepos" },
      { name: "OIDC authentication" },
      { name: "CI/CD" },
    ],
  },
  {
    title: "Documentation & Knowledge",
    tools: [
      { name: "Quartz" },
      { name: "Markdown" },
      { name: "MkDocs" },
    ],
  },
];

const certBadges: Record<string, string> = {
  Kubernetes: "CKAD",
  Helm: "CKAD",
};

const deepening = [
  { name: "CKA Certification", context: "40-day certification course in progress - cluster architecture, networking, storage, and troubleshooting" },
  { name: "CKS Certification", context: "Kubernetes security specialist - cluster hardening, supply chain security, runtime security" },
  { name: "AI-Augmented DevSecOps", context: "Using Claude Code and LLM agents for IaC generation, incident triage, and automated synthesis" },
  { name: "Zero-Trust Architecture", context: "BeyondCorp patterns, identity-based access, and Teleport for infrastructure access" },
  { name: "Platform Engineering", context: "Internal developer platforms, self-service infrastructure, and golden paths" },
  { name: "FinTech & Web3 Infrastructure", context: "High-traffic FinTech platforms, decentralized node infrastructure, smart contract security, and blockchain observability" },
];

const allLabel = "All";

const Skills = () => {
  useDocumentTitle("Engineering Stack");
  const [active, setActive] = useState(allLabel);
  const [search, setSearch] = useState("");
  const [selectedTool, setSelectedTool] = useState<string | null>(null);

  const filteredCategories = useMemo(() => {
    const base =
      active === allLabel
        ? categories
        : categories.filter((c) => c.title === active);

    if (!search.trim()) return base;

    const q = search.toLowerCase();
    return base
      .map((cat) => ({
        ...cat,
        tools: cat.tools.filter((t) => t.name.toLowerCase().includes(q)),
      }))
      .filter((cat) => cat.tools.length > 0);
  }, [active, search]);

  const totalTools = new Set(
    categories.flatMap((c) => c.tools.map((t) => t.name))
  ).size;

  const handleToolClick = (toolName: string) => {
    if (!toolProjects[toolName]) return;
    setSelectedTool(selectedTool === toolName ? null : toolName);
  };

  return (
    <>
      <div
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
        }}
        aria-hidden="false"
      >
        <h1>Engineering Stack | Rakesh Kadam, DevSecOps Lead & Cloud Engineer</h1>
        <p>
          Docker, Kubernetes, CKAD, Helm, Traefik, NestJS, Teleport, Loki, Grafana,
          Prometheus, Cloudflare, Zero-Trust, Terraform, AWS, ECS, EKS, ECR, RDS, S3,
          Lambda, API Gateway, WAF, CloudFront, Claude Code, LLM Agents, Python,
          TypeScript, Node.js, Bash, PostgreSQL, Redis, MongoDB, REST, gRPC, WebSockets,
          GitHub Actions, Bitbucket Pipelines, Jenkins, GitLab CI, Nx monorepos,
          OIDC authentication, Quartz, Markdown, GitOps, CI/CD, DevSecOps, Observability,
          Distributed Systems, FinTech Infrastructure, Web3 Infrastructure.
        </p>
      </div>

      <div className="py-24 px-6 text-light-text dark:text-text-primary">
        <div className="mx-auto max-w-5xl">
          {/* Header */}
          <div className="mb-14 text-center">
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.2em] text-light-muted dark:text-text-faint">
              Engineering Stack
            </p>
            <h1 className="mb-4 text-3xl md:text-4xl font-bold tracking-tight">
              Tools I{" "}
              <span className="text-accent">Actually Use</span>
            </h1>
            <p className="mx-auto max-w-2xl text-base md:text-lg text-light-muted dark:text-text-muted leading-relaxed">
              {totalTools} tools across {categories.length} categories. Every
              one used in real infrastructure, documented in{" "}
              <a
                href="https://brain.rakeshkadam.me"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-accent-muted transition-colors underline underline-offset-2 decoration-accent/30"
              >
                my Brain
              </a>{" "}
              with source code and runbooks.
            </p>
          </div>

          {/* Search */}
          <div className="mb-8 text-center">
            <div className="relative inline-block w-full md:w-1/2">
              <FiSearch
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-light-muted dark:text-text-faint"
              />
              <input
                type="text"
                placeholder="Search tools..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setSelectedTool(null);
                }}
                className="w-full rounded-lg border border-light-border bg-light-surface pl-10 pr-4 py-3 text-sm text-light-text placeholder:text-light-muted focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent/40 dark:border-border-subtle dark:bg-surface-1 dark:text-text-primary dark:placeholder:text-text-faint"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-light-muted hover:text-light-text dark:text-text-faint dark:hover:text-text-primary"
                >
                  <FiX size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Filter tabs */}
          <div className="mb-14">
            <div
              className="flex flex-wrap justify-center gap-2"
              role="tablist"
            >
              <button
                role="tab"
                aria-selected={active === allLabel}
                onClick={() => {
                  setActive(allLabel);
                  setSelectedTool(null);
                }}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
                  active === allLabel
                    ? "bg-light-text text-light-surface dark:bg-text-primary dark:text-surface-0"
                    : "border border-light-border bg-light-surface text-light-muted hover:border-light-border-strong dark:border-border-subtle dark:bg-surface-1 dark:text-text-muted dark:hover:border-border-hover"
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.title}
                  role="tab"
                  aria-selected={active === cat.title}
                  onClick={() => {
                    setActive(cat.title);
                    setSelectedTool(null);
                  }}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
                    active === cat.title
                      ? "bg-light-text text-light-surface dark:bg-text-primary dark:text-surface-0"
                      : "border border-light-border bg-light-surface text-light-muted hover:border-light-border-strong dark:border-border-subtle dark:bg-surface-1 dark:text-text-muted dark:hover:border-border-hover"
                  }`}
                >
                  {cat.title}
                </button>
              ))}
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-col gap-10">
            {filteredCategories.length === 0 && (
              <p className="py-16 text-center text-light-muted dark:text-text-muted">
                No tools match &quot;{search}&quot;
              </p>
            )}

            {filteredCategories.map((cat) => {
              const selectedInCategory = cat.tools.some(
                (t) => t.name === selectedTool
              );

              return (
                <section key={cat.title}>
                  <p className="mb-4 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-light-muted dark:text-text-faint">
                    {cat.title}
                  </p>
                  <div className="flex flex-wrap gap-2.5">
                    {cat.tools.map((tool) => {
                      const projects = toolProjects[tool.name];
                      const cert = certBadges[tool.name];
                      const isSelected = selectedTool === tool.name;
                      const hasProjects = !!projects;

                      return (
                        <button
                          key={tool.name}
                          onClick={() => handleToolClick(tool.name)}
                          className={`inline-flex items-center gap-1.5 rounded-lg border px-3.5 py-2 text-sm font-medium transition-all ${
                            isSelected
                              ? "border-accent/40 bg-accent-dim text-accent"
                              : hasProjects
                                ? "border-light-border bg-light-surface text-light-text hover:border-accent/30 cursor-pointer dark:border-border-subtle dark:bg-surface-1 dark:text-text-primary dark:hover:border-accent/20"
                                : "border-light-border bg-light-surface text-light-text dark:border-border-subtle dark:bg-surface-1 dark:text-text-primary cursor-default"
                          }`}
                        >
                          {tool.name}
                          {projects && (
                            <span className="text-[11px] tabular-nums text-light-muted dark:text-text-faint">
                              {projects.length}
                            </span>
                          )}
                          {cert && (
                            <span className="rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-bold leading-none text-accent">
                              {cert}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Inline project cross-reference */}
                  {selectedInCategory && selectedTool && toolProjects[selectedTool] && (
                    <div className="mt-4 rounded-xl border border-accent/20 bg-accent-dim p-5">
                      <div className="mb-3 flex items-center justify-between">
                        <p className="text-sm">
                          <span className="font-semibold">{selectedTool}</span>
                          <span className="text-light-muted dark:text-text-muted">
                            {" "}
                            is used in{" "}
                            {toolProjects[selectedTool].length} project
                            {toolProjects[selectedTool].length !== 1 ? "s" : ""}
                          </span>
                        </p>
                        <button
                          onClick={() => setSelectedTool(null)}
                          className="rounded-md p-1 text-light-muted transition-colors hover:text-light-text dark:text-text-faint dark:hover:text-text-primary"
                          aria-label="Close"
                        >
                          <FiX size={14} />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {toolProjects[selectedTool].map((name) => (
                          <a
                            key={name}
                            href={PROJECTS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex items-center gap-1 rounded-lg border border-light-border bg-light-surface px-3 py-1.5 text-sm font-medium text-light-text transition-colors hover:border-accent/30 dark:border-border-subtle dark:bg-surface-1 dark:text-text-primary"
                          >
                            {name}
                            <FiArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-accent" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              );
            })}
          </div>

          {/* Currently Deepening */}
          <div className="mt-14 border-t border-light-border pt-10 dark:border-border-subtle">
            <p className="mb-6 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-light-muted dark:text-text-faint">
              Currently Deepening
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {deepening.map((item) => (
                <div
                  key={item.name}
                  className="flex items-start gap-3 rounded-lg border border-accent/15 bg-light-surface p-4 dark:bg-surface-1"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <div>
                    <p className="text-sm font-semibold text-accent">
                      {item.name}
                    </p>
                    <p className="mt-1 text-xs text-light-muted dark:text-text-muted leading-relaxed">
                      {item.context}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Skills;
